<script lang="ts">
  // Checkbox — the design-system single-checkbox primitive
  // (wiki/feature-design-system.md §Component primitives). A labelled boolean
  // toggle: a native `<input type="checkbox">` (so it is keyboard- and
  // screen-reader-correct for free) styled with the accent token, plus a label
  // and an optional one-line description. Composed by CheckboxGroup for the
  // multi-select case; usable standalone for a lone opt-in. No bespoke per-page
  // checkbox chrome.
  //
  // The checked state is `bindable`; `onChange` fires the new value after a
  // toggle. The label is a typed string prop, never a slot.
  interface Props {
    /** Stable id used to associate the input with its label / description. */
    id: string;
    /** The visible label text. */
    label: string;
    /** Optional one-line description shown under the label. */
    description?: string;
    /** Checked state (bindable). */
    checked?: boolean;
    /** Fired after the checked state toggles. */
    onChange?: (checked: boolean) => void;
    /** Disable the control. */
    disabled?: boolean;
    class?: string;
  }

  let {
    id,
    label,
    description,
    checked = $bindable(false),
    onChange,
    disabled = false,
    class: klass = '',
  }: Props = $props();

  const descId = $derived(description ? `${id}-desc` : undefined);

  function onInput(event: Event): void {
    if (disabled) return;
    checked = (event.currentTarget as HTMLInputElement).checked;
    onChange?.(checked);
  }
</script>

<div class="vd-checkbox {klass}" class:vd-checkbox--disabled={disabled}>
  <input
    {id}
    type="checkbox"
    class="vd-checkbox__input"
    checked={checked}
    {disabled}
    aria-describedby={descId}
    onchange={onInput}
  />
  <label class="vd-checkbox__label" for={id}>
    <span class="vd-checkbox__text">{label}</span>
    {#if description}
      <span class="vd-checkbox__desc" id={descId}>{description}</span>
    {/if}
  </label>
</div>

<style lang="scss">
  .vd-checkbox {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    font-family: var(--font-ui);
  }

  /* Dims the LABEL, not the control. Opacity on the row composites the box and
     its tick together, which is what made a locked-on checkbox unreadable. */
  .vd-checkbox--disabled .vd-checkbox__label {
    opacity: 0.6;
  }

  .vd-checkbox__input {
    flex: none;
    width: 1.05rem;
    height: 1.05rem;
    margin: 0.1rem 0 0;
    accent-color: var(--accent);
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
    }

    &:focus-visible {
      outline: 2px solid var(--accent-text);
      outline-offset: 2px;
    }

    /* WCAG 2.5.8: a touch target clears 24px. */
    @media (pointer: coarse) {
      width: 1.5rem;
      height: 1.5rem;
      margin-top: 0;
    }
  }

  .vd-checkbox__label {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    cursor: pointer;
    min-width: 0;
  }

  .vd-checkbox--disabled .vd-checkbox__label {
    cursor: not-allowed;
  }

  .vd-checkbox__text {
    color: var(--ink);
    font-size: var(--text-body);
    line-height: 1.3;
  }

  .vd-checkbox__desc {
    color: var(--ink-soft);
    font-size: var(--text-meta);
    line-height: 1.35;
  }
</style>
